/* CascadeDeleteDialog — Superadmin bevestigingsdialoog met impact-preview.
   Toont hoeveel gekoppelde data (kinderen, contracten, facturen, etc.) er
   meegaat, met verplichte reden en cascade-toggle. */
import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { AlertTriangle, Trash2 } from "lucide-react";
import { toast } from "sonner";

const IMPACT_LABELS = {
  kinderen: "Kinderen",
  groepen: "Groepen",
  contracten: "Contracten",
  actieve_contracten: "Actieve contracten",
  facturen: "Facturen",
  openstaande_facturen: "Openstaande facturen",
  presentie: "Presentie-records",
  medewerkers: "Medewerker-koppelingen",
  wachtlijst: "Wachtlijst-inschrijvingen",
  leidsters: "Gekoppelde leidsters",
  schriftje: "Schriftje-notities",
  daglog: "Daglog-events",
  mentorbericht: "Mentor-berichten",
  mentor_van_kinderen: "Mentor van kinderen",
  user_accounts: "User accounts",
};

const RESOURCE_LABEL = {
  locations: "locatie",
  groups: "groep",
  children: "kind",
  parents: "ouder",
  medewerkers: "medewerker",
};

export default function CascadeDeleteDialog({
  open, onClose, resource, resourceId, resourceLabel, onDeleted,
}) {
  /* resource: "locations" | "groups"
     resourceLabel: naam om te tonen (bijv. "Kinderopvang De Zonnebloem") */
  const [impact, setImpact] = useState(null);
  const [loading, setLoading] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !resourceId) return;
    setImpact(null); setReason(""); setLoading(true);
    api.get(`/${resource}/${resourceId}/impact`)
      .then(r => setImpact(r.data))
      .catch(e => toast.error(formatApiError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [open, resource, resourceId]);

  const hasImpact = impact && impact.totaal > 0;

  const doDelete = async (cascade) => {
    if (!reason.trim()) {
      toast.error("Reden verplicht voor audit-log");
      return;
    }
    setBusy(true);
    try {
      await api.delete(`/${resource}/${resourceId}`, {
        params: { reason: reason.trim(), cascade: cascade ? "true" : "false" },
      });
      toast.success(cascade ? "Verwijderd (cascade)" : "Verwijderd");
      onDeleted?.();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const rows = impact ? Object.entries(IMPACT_LABELS)
    .filter(([k]) => impact[k] != null && impact[k] > 0)
    .map(([k, label]) => [label, impact[k]]) : [];

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-white max-w-lg" data-testid="cascade-delete-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-700">
            <AlertTriangle className="h-5 w-5" />
            Verwijder {RESOURCE_LABEL[resource] || resource}
          </DialogTitle>
          <DialogDescription>
            {resourceLabel} — permanente actie, wordt vastgelegd in het audit-logboek.
          </DialogDescription>
        </DialogHeader>

        {loading && <div className="text-sm text-neutral-500 py-4">Impact laden…</div>}

        {impact && !loading && (
          <div className="space-y-3">
            {hasImpact ? (
              <div className="p-3 rounded-md border-2 border-amber-300 bg-amber-50" data-testid="cascade-impact">
                <div className="text-xs font-semibold text-amber-800 uppercase tracking-widest mb-2">
                  Gekoppelde data (wordt meegewijzigd bij cascade)
                </div>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                  {rows.map(([label, val]) => (
                    <div key={label} className="flex justify-between">
                      <span className="text-neutral-700">{label}</span>
                      <span className="font-mono font-semibold text-amber-900">{val}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="p-3 rounded-md border border-emerald-200 bg-emerald-50 text-sm text-emerald-800" data-testid="cascade-empty">
                Geen gekoppelde data — veilig te verwijderen.
              </div>
            )}

            <div>
              <Label htmlFor="cascade-reason">Reden (verplicht — audit-log)</Label>
              <Input
                id="cascade-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Bijv. locatie gesloten per 1-1-2027"
                data-testid="cascade-reason"
                className="mt-1"
              />
            </div>
          </div>
        )}

        <DialogFooter className="flex flex-col-reverse sm:flex-row gap-2">
          <Button variant="ghost" onClick={onClose} disabled={busy}>Annuleren</Button>
          {!hasImpact && (
            <Button
              onClick={() => doDelete(false)}
              disabled={busy || !reason.trim()}
              data-testid="cascade-delete-safe"
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              <Trash2 className="h-4 w-4 mr-1.5" /> Verwijderen
            </Button>
          )}
          {hasImpact && (
            <Button
              onClick={() => doDelete(true)}
              disabled={busy || !reason.trim()}
              data-testid="cascade-delete-force"
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              <Trash2 className="h-4 w-4 mr-1.5" />
              Cascade verwijderen ({impact.totaal})
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
